import { savedSearchesPath } from 'src/config';
import { useCallback } from 'react';

import { useTranslations } from '@vocab/react';
import {
  Box,
  HiddenVisually,
  IconCritical,
  IconInfo,
  IconMail,
  IconTick,
  Loader,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import {
  selectSaveSearchErrorMessage,
  selectSaveSearchStatus,
} from 'src/store/selectors/index';

import SaveSearchButton from 'src/components/SaveSearchButton/SaveSearchButton';
import { useSelector } from 'src/store/react';
import { SavedSearchStatus } from 'src/store/saveSearch/saveSearch';
import translations from './.vocab';
import { useFetchSavedSearches } from 'src/modules/hooks/SavedSearch';

interface SaveSearchRenderProps {
  icon: JSX.Element;
  text: JSX.Element;
  cta?: JSX.Element;
  dataAutomation: string;
}

interface SaveSearchRenderArgs {
  status: SavedSearchStatus;
  isSearchSaved: boolean;
  errorMessage?: string;
  loading: boolean;
}
const { SAVED, ERROR, EXCEEDED, INVALID_LOCATION, SAVING, DUPLICATE } =
  SavedSearchStatus;

const useManageLink = () => {
  const { t } = useTranslations(translations);
  return (
    <Text>
      <TextLink href={savedSearchesPath}>
        {t('Manage your Saved searches')}
      </TextLink>
    </Text>
  );
};

const useSaveSearchButton = (status: SavedSearchStatus) => {
  const isSaving = status === SAVING;
  const buttonProps = {
    dataAutomation: 'saveSearchButton',
    loading: isSaving,
  };

  const { t } = useTranslations(translations);
  return (
    <SaveSearchButton {...buttonProps}>
      {isSaving ? t('Saving') : t('Save')}
    </SaveSearchButton>
  );
};

const useGetMessageProps = ({
  status,
  isSearchSaved,
  errorMessage,
}: SaveSearchRenderArgs): SaveSearchRenderProps => {
  const { t } = useTranslations(translations);
  const hiddenConfirmationRef = useCallback((node) => {
    node?.focus();
  }, []);
  const manageLink = useManageLink();
  const saveSearchButton = useSaveSearchButton(status);

  if (status === SAVED) {
    const savedText = t('We have saved your search');
    return {
      icon: <IconTick tone="positive" size="large" />,
      text: (
        <>
          <Text tone="positive">{savedText}</Text>
          <HiddenVisually>
            <Box ref={hiddenConfirmationRef} tabIndex={0}>
              {savedText}
            </Box>
          </HiddenVisually>
        </>
      ),
      cta: manageLink,
      dataAutomation: 'success',
    };
  }

  if (isSearchSaved) {
    return {
      icon: <IconInfo size="large" />,
      text: <Text>{t('You have saved this search before')}</Text>,
      cta: manageLink,
      dataAutomation: 'already-saved',
    };
  }

  if (status === ERROR) {
    return {
      icon: <IconCritical tone="critical" size="large" />,
      text: <Text tone="critical">{t('There is something wrong')}</Text>,
      cta: undefined,
      dataAutomation: 'error',
    };
  }

  if (status === EXCEEDED) {
    return {
      icon: <IconInfo tone="info" size="large" />,
      text: <Text tone="info">{errorMessage}.</Text>,
      cta: manageLink,
      dataAutomation: 'exceeded',
    };
  }

  if ([DUPLICATE, INVALID_LOCATION].includes(status)) {
    return {
      icon: <IconCritical tone="critical" size="large" />,
      text: <Text tone="critical">{errorMessage}</Text>,
      cta: manageLink,
      dataAutomation: status === INVALID_LOCATION ? 'invalid' : 'duplicate',
    };
  }

  return {
    icon: <IconMail size="large" />,
    text: <Text>{t('Save this search and get new jobs...')}</Text>,
    cta: saveSearchButton,
    dataAutomation: 'save',
  };
};

const useRenderMessage = ({
  status,
  isSearchSaved,
  errorMessage,
  loading,
}: SaveSearchRenderArgs) => {
  const messageProps = useGetMessageProps({
    status,
    isSearchSaved,
    errorMessage,
    loading,
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        marginY="xxlarge"
      >
        <Loader size="small" />
      </Box>
    );
  }

  const { dataAutomation, icon, text, cta } = messageProps;

  return (
    <Stack space="large" data={{ automation: `saveSearch-${dataAutomation}` }}>
      <Stack space="medium">
        {icon}
        {text}
      </Stack>
      {cta}
    </Stack>
  );
};

const SaveSearch = () => {
  const status = useSelector(selectSaveSearchStatus);
  const errorMessage = useSelector(selectSaveSearchErrorMessage);
  const { loading, isSearchSaved } = useFetchSavedSearches();

  return (
    <Box data={{ automation: 'sideSavePanel' }}>
      {useRenderMessage({ loading, status, isSearchSaved, errorMessage })}
    </Box>
  );
};

export default SaveSearch;
